import React, { useState, useEffect, useMemo, useRef } from 'react';
import './DataExplorer.css';
import Sidebar from './Sidebar';
import { getTournamentType } from '../utils/dataProcessor';

// Added apiEndpoint prop: if provided, component will fetch data from the API instead of relying solely on passed-in data.
// Assumes the API returns an array of objects shaped like existing rows.
const DataExplorer = ({ data, filters, onFilterChange, apiEndpoint }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [remoteData, setRemoteData] = useState(null); // data fetched from API
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  // Refresh handler to manually trigger re-fetch
  const refreshData = () => {
    if (!apiEndpoint) return;
    fetchData();
  };

  // Fetch function separated for reuse (initial + manual refresh)
  const fetchData = () => {
    if (!apiEndpoint) return;
    // Abort any previous request
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    fetch(apiEndpoint, { signal: controller.signal })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Request failed: ${res.status} ${res.statusText}`);
        // Assume JSON response
        const json = await res.json();
        if (!Array.isArray(json)) {
          throw new Error('API response is not an array');
        }
        setRemoteData(json);
      })
      .catch(err => {
        if (err.name === 'AbortError') return; // Ignore aborts
        console.error('Data fetch error:', err);
        setError(err.message || 'Unknown error fetching data');
        setRemoteData([]); // Set to empty to avoid undefined references
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Initial + dependency-based fetch
  useEffect(() => {
    fetchData();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiEndpoint]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Simple data filtering - minimal processing
  const baseData = remoteData !== null ? remoteData : data; // prefer remote data if fetched
  const filteredData = useMemo(() => {
    if (!baseData || baseData.length === 0) {
      return [];
    }
    try {
      return baseData.filter(row => {
        if (!row) return false;
        
        const tournamentMatch = !filters.tournaments.length || filters.tournaments.includes(row.Tournament);
        const tournamentTypeMatch = !filters.tournamentTypes.length || filters.tournamentTypes.includes(getTournamentType(row));
        const dateMatch = !filters.dates.length || filters.dates.includes(row.Date);
        const courtMatch = !filters.courts.length || filters.courts.includes(row['Court Name']);
        const graphicMatch = !filters.graphics.length || filters.graphics.includes(row['Graphic Type']);
        const statsDisplayedMatch = !filters.statsDisplayed?.length || filters.statsDisplayed.includes(row['Stats Displayed']); 
        const sponsorMatch = !filters.sponsors?.length || filters.sponsors.includes(row.Sponsor);
        const isOnAir = row.IsOnAir === true || row.IsOnAir === 'true';
        const isVisible = row.IsVisible === true || row.IsVisible === 'true';
        
          return tournamentMatch && tournamentTypeMatch && dateMatch && courtMatch && graphicMatch && statsDisplayedMatch && sponsorMatch;
      });
    } catch (error) {
      console.error('Error filtering data:', error);
      return [];
    }
  }, [baseData, filters]);

  // Early return if no data
  if (loading) {
    return (
      <div className="data-explorer-loading">
        <p>Loading data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="data-explorer-error">
        <p>Error loading data: {error}</p>
        <button className="retry-button" onClick={refreshData}>Retry</button>
      </div>
    );
  }

  if (!baseData || baseData.length === 0) {
    return (
      <div className="no-data">
        <p>No data available</p>
        {apiEndpoint && <button className="retry-button" onClick={refreshData}>Refresh</button>}
      </div>
    );
  }

  // Simple pagination
  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, filteredData.length);
  const currentData = filteredData.slice(startIndex, endIndex);

  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0s';
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  };

  const downloadCSV = () => {
    try {
      if (filteredData.length === 0) {
        alert('No data to download');
        return;
      }

      // Get headers from the first row
      const headers = Object.keys(filteredData[0] || {});
      
      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...filteredData.map(row => 
          headers.map(header => {
            const value = row[header];
            if (value === null || value === undefined) return '';
            const stringValue = String(value);
            // Escape quotes and wrap in quotes if contains comma, quote, or newline
            return stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n') 
              ? `"${stringValue.replace(/"/g, '""')}"` 
              : stringValue;
          }).join(',')
        )
      ].join('\n');

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sponsor_data_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('Error downloading file. Please try again.');
    }
  };

  return (
    <div className="data-explorer-container">
      <Sidebar 
        data={baseData} 
        filters={filters} 
        onFilterChange={onFilterChange} 
      />
      
      <div className="data-explorer-content">
        {/* Simple data table */}
        <div className="explorer-section">
          <div className="section-header">
            <div className="table-controls">
            <div className="controls-left">
              <label>
                Records per page:
                <select 
                  value={itemsPerPage} 
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="page-size-select"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </label>
              <p className="data-info">
                Showing {startIndex + 1}-{endIndex} of {filteredData.length.toLocaleString()} records
              </p>
            </div>
            <div className="controls-right">
              {apiEndpoint && (
                <button 
                  className="refresh-button" 
                  onClick={refreshData} 
                  disabled={loading}
                  style={{ marginRight: '8px' }}
                >
                  {loading ? 'Refreshing...' : 'Refresh'}
                </button>
              )}
              <button 
                className="download-button primary" 
                onClick={downloadCSV}
                disabled={filteredData.length === 0}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '4px' }}>
                  <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M7 10L12 15L17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M12 15V3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Download CSV ({filteredData.length.toLocaleString()} records)
              </button>
            </div>
          </div>
          </div>

          {/* Data table */}
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Tournament</th>
                  <th>Date</th>
                  <th>Court</th>
                  <th>Graphic Type</th>
                  <th>Stats Displayed</th>
                  <th>Sponsor</th>
                  <th>Duration</th>
                  <th>On Air</th>
                  <th>Visible</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((row, index) => {
                  const isOnAir = row.IsOnAir === true || row.IsOnAir === 'true';
                  const isVisible = row.IsVisible === true || row.IsVisible === 'true';
                  const isActive = isOnAir && isVisible;
                  return (
                  <tr key={startIndex + index} className={isActive ? 'row-active' : undefined} style={isActive ? { backgroundColor: '#192442' } : undefined}>
                    <td>{row.Tournament || '-'}</td>
                    <td>{row.Date ? new Date(row.Date).toLocaleDateString() : '-'}</td>
                    <td>{row['Court Name'] || '-'}</td>
                    <td>{row['Graphic Type'] || '-'}</td>
                    <td>{row['Stats Displayed'] || '-'}</td>
                    <td>{row.Sponsor || '-'}</td>
                    <td>{formatDuration(row.Duration_Seconds)}</td>
                    <td>{isOnAir ? 'Yes' : 'No'}</td>
                    <td>{isVisible ? 'Yes' : 'No'}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Simple pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                className="pagination-button"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                First
              </button>
              <button 
                className="pagination-button"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>
              <button 
                className="pagination-button"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
              <button 
                className="pagination-button"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataExplorer; 